/**
 * @desc 회원 관련된 API 모음집
 */
import { v2AxiosInstance } from "apis/indexApi";

/**
 * @desc SNS 인증을 통해 사용자 정보를 가져오는 함수
 * @param {string} sns - SNS 플랫폼 이름 (예: 'naver')
 * @param {string} code - 인증 코드
 * @param {string} redirectURL - 인증 후 리디렉션 URL
 * @param {string} [state] - 상태 값 (옵션, 'naver'일 때만 필요)
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const postAccountUserOauthSNS = async (
  sns,
  code,
  redirectURL,
  state
) => {
  // 요청 바디 구성
  const body = {
    code,
    redirectURL,
    ...(sns === "naver" && { state }),
  };

  // 서버로부터 데이터 요청
  const { data } = await v2AxiosInstance.post(
    `/account/user/oauth/${sns}`,
    body
  );

  return data;
};

/**
 * @desc 회원 가입 (일반/사업자)
 * @param {string} role - 일반, 사업자 구분 값
 * @param {object} body - 회원 정보
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const postAccountUserSignUpRole = async (role, body) => {
  const { data } = await v2AxiosInstance.post(
    `/account/user/signup/${role}`,
    body
  );
  return data;
};

/**
 * @desc 회원 정보 조회
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const getAccountUser = async () => {
  const { data } = await v2AxiosInstance.get(`/account/user`);
  return data;
};

/**
 * @desc 회원 프로필 이미지 등록
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const patchAccountUserImages = async (uploadPath) => {
  const { data } = await v2AxiosInstance.patch(`/account/user/images`, {
    uploadPath: uploadPath,
  });
  return data;
};

/**
 * @desc 회원 프로필 이미지 삭제
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const deleteAccountUserImages = async () => {
  const { data } = await v2AxiosInstance.delete(`/account/user/images`);
  return data;
};

/**
 * @desc 회원 정보 수정
 * @param {object} body - 회원 정보
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const patchAccountUserRole = async (role, body) => {
  const { data } = await v2AxiosInstance.patch(`/account/user/${role}`, body);
  return data;
};
/**
 * @desc 사업자 전환
 * @param {object} body - 사업자 정보
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const postAccountUserBizConversion = async (body) => {
  const { data } = await v2AxiosInstance.post(
    `/account/user/biz-conversion`,
    body
  );
  return data;
};

/**
 * @desc 회원 로그아웃
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const postAccountUserLogout = async () => {
  const { data } = await v2AxiosInstance.post(`/account/user/logout`);
  return data;
};

/**
 * @desc 회원 탈퇴 여부
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const getAccountUserDeletable = async () => {
  const { data } = await v2AxiosInstance.get(`/account/user/deletable`);
  return data;
};

/**
 * @desc 회원 탈퇴
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const deleteAccountUser = async () => {
  const { data } = await v2AxiosInstance.delete(`/account/user`);
  return data;
};

/**
 * @desc 토큰 재발행
 * @returns {Promise<Object>} - 서버 응답 데이터
 */
export const postTokenReissue = async () => {
  const { data } = await v2AxiosInstance.post(`/token/reissue/cookie`);
  return data;
};

/**
 * @desc 사업자 계정 가입 가능 여부 검증
 * @param {*} bizNum 사업자 번호
 * @returns {Promise<void>}
 */
export const getAccountBizNumber = async (bizNum) => {
  const { data } = await v2AxiosInstance.get(
    `/account/biz/validation?bizNum=${bizNum}`
  );
  return data;
};
